import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { 
  MapPin, Calendar, Clock, Building2, Users, Store, 
  ChevronDown, Plus, Filter, RefreshCw, Loader2, 
  HandHelping, UserPlus, CheckCircle, AlertCircle,
  X, Eye, MapPinned
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Retailer logos/colors for visual differentiation
const RETAILER_COLORS = {
  binnys: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
  specs: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
  total_wine: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
  tamuras: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
  abc_fl: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
  bevmo: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-200' },
  costco: { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-200' },
  sams: { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-200' },
  target: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
};

function MeetingCard({ meeting, onVolunteer, isAuthenticated }) {
  const colors = RETAILER_COLORS[meeting.retailer_id] || { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200' };
  const [volunteering, setVolunteering] = useState(false);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const handleVolunteer = async () => {
    setVolunteering(true);
    await onVolunteer(meeting.id);
    setVolunteering(false);
  };

  return (
    <div 
      className={`bg-white rounded-xl border ${colors.border} p-5 hover:shadow-lg transition-all duration-200`}
      data-testid={`meeting-card-${meeting.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center`}>
            <Store className={`w-6 h-6 ${colors.text}`} />
          </div>
          <div>
            <h3 className="font-semibold text-navy">{meeting.retailer_name}</h3>
            <p className="text-sm text-slate-500">{meeting.retailer_type}</p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
          meeting.status === 'Confirmed' ? 'bg-green-100 text-green-700' :
          meeting.status === 'Scheduled' ? 'bg-blue-100 text-blue-700' :
          meeting.status === 'Completed' ? 'bg-slate-100 text-slate-600' :
          'bg-yellow-100 text-yellow-700'
        }`}>
          {meeting.status}
        </span>
      </div>

      {/* Details */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 text-sm">
          <MapPin className="w-4 h-4 text-copper" />
          <span className="text-slate-700">{meeting.location || meeting.state_name}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="w-4 h-4 text-copper" />
          <span className="text-slate-700">{formatDate(meeting.meeting_date)}</span>
          {meeting.meeting_time && (
            <>
              <Clock className="w-4 h-4 text-copper ml-2" />
              <span className="text-slate-700">{meeting.meeting_time}</span>
            </>
          )}
        </div>
        {meeting.buyer_name && (
          <div className="flex items-center gap-2 text-sm">
            <Users className="w-4 h-4 text-copper" />
            <span className="text-slate-700">{meeting.buyer_name}</span>
            {meeting.buyer_title && <span className="text-slate-500">• {meeting.buyer_title}</span>}
          </div>
        )}
        {meeting.supplier_name && (
          <div className="flex items-center gap-2 text-sm">
            <Building2 className="w-4 h-4 text-copper" />
            <span className="text-slate-700">{meeting.supplier_name}</span>
          </div>
        )}
      </div>

      {/* Meeting Type Tag */}
      <div className="flex items-center justify-between">
        <span className={`px-3 py-1 rounded-lg text-xs font-medium ${colors.bg} ${colors.text}`}>
          {meeting.meeting_type}
        </span>

        {/* Needs Representation Badge */}
        {meeting.needs_representation && !meeting.represented_by && isAuthenticated && (
          <button
            onClick={handleVolunteer}
            disabled={volunteering}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-copper text-white text-xs font-medium rounded-lg hover:bg-copper-dark transition-colors"
            data-testid={`volunteer-btn-${meeting.id}`}
          >
            {volunteering ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <HandHelping className="w-3 h-3" />
            )}
            Volunteer
          </button>
        )}
        {meeting.represented_by && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-lg">
            <CheckCircle className="w-3 h-3" />
            Rep: {meeting.represented_by}
          </span>
        )}
      </div>
    </div>
  );
}

function AddMeetingModal({ isOpen, onClose, retailers, states, meetingTypes, onSubmit }) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    retailer_id: '',
    state: '',
    location: '',
    meeting_date: '',
    meeting_time: '',
    buyer_name: '',
    buyer_title: '',
    meeting_type: 'Introduction',
    supplier_name: user?.company || '',
    notes: '',
    is_shared: true,
    needs_representation: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await onSubmit(formData);
      onClose();
      setFormData({
        retailer_id: '',
        state: '',
        location: '',
        meeting_date: '',
        meeting_time: '',
        buyer_name: '',
        buyer_title: '',
        meeting_type: 'Introduction',
        supplier_name: user?.company || '',
        notes: '',
        is_shared: true,
        needs_representation: false
      });
    } catch (err) {
      setError(err.message || 'Failed to create meeting');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-meeting-modal">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-navy">Add Trade Meeting</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-navy mb-1">Retailer *</label>
              <select
                value={formData.retailer_id}
                onChange={(e) => setFormData({ ...formData, retailer_id: e.target.value })}
                required
                className="input-field"
                data-testid="meeting-retailer-select"
              >
                <option value="">Select retailer...</option>
                {retailers.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-navy mb-1">State *</label>
              <select
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                required
                className="input-field"
                data-testid="meeting-state-select"
              >
                <option value="">Select state...</option>
                {states.map(s => (
                  <option key={s.code} value={s.code}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-navy mb-1">Location / Store</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="e.g., Chicago - Lincoln Park"
              className="input-field"
              data-testid="meeting-location-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-navy mb-1">Date *</label>
              <input
                type="date"
                value={formData.meeting_date}
                onChange={(e) => setFormData({ ...formData, meeting_date: e.target.value })}
                required
                className="input-field"
                data-testid="meeting-date-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-navy mb-1">Time</label>
              <input
                type="time"
                value={formData.meeting_time}
                onChange={(e) => setFormData({ ...formData, meeting_time: e.target.value })}
                className="input-field"
                data-testid="meeting-time-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-navy mb-1">Buyer Name</label>
              <input
                type="text"
                value={formData.buyer_name}
                onChange={(e) => setFormData({ ...formData, buyer_name: e.target.value })}
                placeholder="e.g., John Smith"
                className="input-field"
                data-testid="meeting-buyer-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-navy mb-1">Buyer Title</label>
              <input
                type="text"
                value={formData.buyer_title}
                onChange={(e) => setFormData({ ...formData, buyer_title: e.target.value })}
                placeholder="e.g., Category Manager"
                className="input-field"
                data-testid="meeting-buyer-title-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-navy mb-1">Meeting Type *</label>
              <select
                value={formData.meeting_type}
                onChange={(e) => setFormData({ ...formData, meeting_type: e.target.value })}
                required
                className="input-field"
                data-testid="meeting-type-select"
              >
                {meetingTypes.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-navy mb-1">Your Brand/Company</label>
              <input
                type="text"
                value={formData.supplier_name}
                onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                placeholder="e.g., Coastal Spirits"
                className="input-field"
                data-testid="meeting-supplier-name-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-navy mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional details..."
              rows={2}
              className="input-field resize-none"
              data-testid="meeting-notes-input"
            />
          </div>

          <div className="flex items-center gap-6 pt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_shared}
                onChange={(e) => setFormData({ ...formData, is_shared: e.target.checked })}
                className="w-4 h-4 rounded border-slate-300 text-copper focus:ring-copper"
                data-testid="meeting-shared-checkbox"
              />
              <span className="text-sm text-slate-700">Share with coalition members</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.needs_representation}
                onChange={(e) => setFormData({ ...formData, needs_representation: e.target.checked })}
                className="w-4 h-4 rounded border-slate-300 text-copper focus:ring-copper"
                data-testid="meeting-needs-rep-checkbox"
              />
              <span className="text-sm text-slate-700">Need someone to represent</span>
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 rounded-xl font-medium hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-3 bg-copper text-white rounded-xl font-medium hover:bg-copper-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="submit-meeting-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Add Meeting
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function TradePay() {
  const { user, token, isAuthenticated } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [states, setStates] = useState([]);
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [selectedState, setSelectedState] = useState('');
  const [selectedRetailer, setSelectedRetailer] = useState('');
  const [showNeedsRep, setShowNeedsRep] = useState(false);
  
  // Modal
  const [showAddModal, setShowAddModal] = useState(false);
  
  // Summary data
  const [stateSummary, setStateSummary] = useState([]);
  const [retailerSummary, setRetailerSummary] = useState([]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchMeetings();
  }, [selectedState, selectedRetailer, showNeedsRep]);

  const fetchInitialData = async () => {
    try {
      const [retailersRes, statesRes, typesRes, stateSumRes, retailerSumRes] = await Promise.all([
        fetch(`${API}/trade-meetings/retailers`),
        fetch(`${API}/trade-meetings/states`),
        fetch(`${API}/trade-meetings/meeting-types`),
        fetch(`${API}/trade-meetings/by-state-summary`),
        fetch(`${API}/trade-meetings/by-retailer-summary`)
      ]);

      if (retailersRes.ok) {
        const data = await retailersRes.json();
        setRetailers(data.retailers);
      }
      if (statesRes.ok) {
        const data = await statesRes.json();
        setStates(data.states);
      }
      if (typesRes.ok) {
        const data = await typesRes.json();
        setMeetingTypes(data.types);
      }
      if (stateSumRes.ok) {
        const data = await stateSumRes.json();
        setStateSummary(data.summary);
      }
      if (retailerSumRes.ok) {
        const data = await retailerSumRes.json();
        setRetailerSummary(data.summary);
      }
    } catch (err) {
      console.error('Error fetching initial data:', err);
    }
  };

  const fetchMeetings = async () => {
    setLoading(true);
    try {
      let url = `${API}/trade-meetings/?upcoming_only=true`;
      if (selectedState) url += `&state=${selectedState}`;
      if (selectedRetailer) url += `&retailer_id=${selectedRetailer}`;
      
      if (showNeedsRep) {
        const res = await fetch(`${API}/trade-meetings/needs-representation`);
        if (res.ok) {
          const data = await res.json();
          setMeetings(data.meetings);
        }
      } else {
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          setMeetings(data);
        }
      }
    } catch (err) {
      setError('Failed to load meetings');
      console.error('Error fetching meetings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMeeting = async (formData) => {
    const res = await fetch(`${API}/trade-meetings/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: JSON.stringify({
        ...formData,
        supplier_id: user?.id
      })
    });

    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.detail || 'Failed to create meeting');
    }

    fetchMeetings();
    fetchInitialData(); // Refresh summaries
  };

  const handleVolunteer = async (meetingId) => {
    if (!user) return;
    
    try {
      const res = await fetch(`${API}/trade-meetings/${meetingId}/volunteer?volunteer_name=${encodeURIComponent(user.name)}`, {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (res.ok) {
        fetchMeetings();
      }
    } catch (err) {
      console.error('Error volunteering:', err);
    }
  };

  const handleSeedData = async () => {
    try {
      await fetch(`${API}/trade-meetings/seed-sample-data`, { method: 'POST' });
      fetchMeetings();
      fetchInitialData();
    } catch (err) {
      console.error('Error seeding data:', err);
    }
  };

  return (
    <Layout>
      <div className="bg-slate-50 min-h-screen">
        {/* Hero Section */}
        <section className="bg-navy text-white py-16">
          <div className="container-custom">
            <div className="max-w-3xl">
              <span className="inline-block px-3 py-1 rounded-full bg-copper/20 text-copper text-sm font-medium mb-4">
                Coalition Feature
              </span>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                Supplier Trade Pay Board
              </h1>
              <p className="text-lg text-white/80 mb-6">
                Track upcoming meetings with top beverage retailers across the nation. 
                Coalition members can view, share, and coordinate on retail meetings with 
                Binny's, Total Wine, Costco, and more.
              </p>
              {isAuthenticated && user?.role === 'Supplier' && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="btn-copper inline-flex items-center gap-2"
                  data-testid="add-meeting-hero-btn"
                >
                  <Plus className="w-5 h-5" />
                  Add Your Meeting
                </button>
              )}
              {!isAuthenticated && (
                <Link
                  to="/register"
                  className="btn-copper inline-flex items-center gap-2"
                >
                  <UserPlus className="w-5 h-5" />
                  Join Coalition to Add Meetings
                </Link>
              )}
            </div>
          </div>
        </section>

        <div className="container-custom py-8">
          {/* Summary Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* By State Summary */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="font-semibold text-navy flex items-center gap-2 mb-4">
                <MapPinned className="w-5 h-5 text-copper" />
                Meetings by State
              </h3>
              {stateSummary.length === 0 ? (
                <p className="text-slate-500 text-sm">No upcoming meetings</p>
              ) : (
                <div className="space-y-2">
                  {stateSummary.slice(0, 5).map((s) => (
                    <div 
                      key={s._id} 
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                      onClick={() => setSelectedState(s._id)}
                    >
                      <span className="font-medium text-navy">{s.state_name}</span>
                      <span className="px-2 py-1 bg-copper/10 text-copper rounded-full text-sm font-medium">
                        {s.count} meeting{s.count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* By Retailer Summary */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="font-semibold text-navy flex items-center gap-2 mb-4">
                <Store className="w-5 h-5 text-copper" />
                Meetings by Retailer
              </h3>
              {retailerSummary.length === 0 ? (
                <p className="text-slate-500 text-sm">No upcoming meetings</p>
              ) : (
                <div className="space-y-2">
                  {retailerSummary.slice(0, 5).map((r) => (
                    <div 
                      key={r._id} 
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                      onClick={() => setSelectedRetailer(r._id)}
                    >
                      <span className="font-medium text-navy">{r.retailer_name}</span>
                      <span className="px-2 py-1 bg-copper/10 text-copper rounded-full text-sm font-medium">
                        {r.count} meeting{r.count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 text-slate-700">
                <Filter className="w-4 h-4" />
                <span className="font-medium">Filters:</span>
              </div>

              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-copper/20 focus:border-copper"
                data-testid="filter-state-select"
              >
                <option value="">All States</option>
                {states.map(s => (
                  <option key={s.code} value={s.code}>{s.name}</option>
                ))}
              </select>

              <select
                value={selectedRetailer}
                onChange={(e) => setSelectedRetailer(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-copper/20 focus:border-copper"
                data-testid="filter-retailer-select"
              >
                <option value="">All Retailers</option>
                {retailers.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showNeedsRep}
                  onChange={(e) => setShowNeedsRep(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-copper focus:ring-copper"
                  data-testid="filter-needs-rep-checkbox"
                />
                <span className="text-sm text-slate-700">Needs Representation</span>
              </label>

              <button
                onClick={() => {
                  setSelectedState('');
                  setSelectedRetailer('');
                  setShowNeedsRep(false);
                }}
                className="text-sm text-copper hover:text-copper-dark flex items-center gap-1"
              >
                <RefreshCw className="w-3 h-3" />
                Clear Filters
              </button>

              {isAuthenticated && user?.role === 'Supplier' && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="ml-auto btn-copper-outline text-sm flex items-center gap-1.5"
                  data-testid="add-meeting-filter-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Meeting
                </button>
              )}
            </div>
          </div>

          {/* Meetings Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-copper" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-red-700">{error}</p>
            </div>
          ) : meetings.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
              <Store className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-semibold text-navy text-lg mb-2">No Upcoming Meetings</h3>
              <p className="text-slate-500 mb-6">
                {selectedState || selectedRetailer || showNeedsRep 
                  ? "No meetings match your current filters." 
                  : "Be the first to add a trade meeting!"}
              </p>
              <div className="flex gap-3 justify-center">
                {meetings.length === 0 && !selectedState && !selectedRetailer && (
                  <button
                    onClick={handleSeedData}
                    className="btn-copper-outline text-sm flex items-center gap-2"
                    data-testid="seed-data-btn"
                  >
                    <Eye className="w-4 h-4" />
                    Load Sample Data
                  </button>
                )}
                {isAuthenticated && user?.role === 'Supplier' && (
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="btn-copper text-sm flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Your Meeting
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="meetings-grid">
              {meetings.map((meeting) => (
                <MeetingCard 
                  key={meeting.id} 
                  meeting={meeting} 
                  onVolunteer={handleVolunteer}
                  isAuthenticated={isAuthenticated}
                />
              ))}
            </div>
          )}

          {/* Featured Retailers */}
          <section className="mt-16 mb-8">
            <h2 className="font-heading text-2xl font-bold text-navy mb-6 text-center">
              Top Beverage Retailers We Track
            </h2>
            <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-4">
              {retailers.map(r => {
                const colors = RETAILER_COLORS[r.id] || { bg: 'bg-slate-100', text: 'text-slate-700' };
                return (
                  <button
                    key={r.id}
                    onClick={() => setSelectedRetailer(r.id === selectedRetailer ? '' : r.id)}
                    className={`p-4 rounded-xl text-center transition-all duration-200 ${
                      selectedRetailer === r.id 
                        ? `${colors.bg} ring-2 ring-copper` 
                        : 'bg-white border border-slate-200 hover:border-copper/30'
                    }`}
                  >
                    <Store className={`w-6 h-6 mx-auto mb-2 ${colors.text}`} />
                    <span className="text-xs font-medium text-navy block truncate">{r.name}</span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* CTA for non-members */}
          {!isAuthenticated && (
            <section className="bg-gradient-to-r from-navy to-navy/90 rounded-2xl p-8 text-center text-white mt-8">
              <h3 className="font-heading text-2xl font-bold mb-3">
                Want to Add Your Meetings?
              </h3>
              <p className="text-white/80 mb-6 max-w-xl mx-auto">
                Join the BevRelay coalition to share and coordinate trade meetings with other suppliers. 
                Build relationships and expand your retail presence.
              </p>
              <Link
                to="/register"
                className="btn-copper inline-flex items-center gap-2"
              >
                <UserPlus className="w-5 h-5" />
                Join the Coalition
              </Link>
            </section>
          )}
        </div>
      </div>

      {/* Add Meeting Modal */}
      <AddMeetingModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        retailers={retailers}
        states={states}
        meetingTypes={meetingTypes}
        onSubmit={handleAddMeeting}
      />
    </Layout>
  );
}
